import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ResultCard from "../results/ResultCard";
import CommentItem from "../common/CommentItem";
import {
  useDeleteProjectCommentMutation,
  useAddProjectCommentMutation,
  useEditProjectCommentMutation,
  useLazyGetProjectByIdQuery,
} from "../../redux/slices/api/projectsApiSlice";
import { useState } from "react";
import { handleError } from "../../hooks/useApi";
import notify from "../common/notify";
import { Oval } from "react-loader-spinner";
import { useGetUserQuery } from "../../redux/slices/api/userApi";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import { useRef } from "react";
import { selectGeneral, setShowAuthModal } from "../../redux/slices/generalSlice";
import { scroller } from "react-scroll";
import { useSearchParams } from "react-router-dom";
import "react-quill/dist/quill.snow.css"; 
import ReactQuill from "react-quill";

const DiscussionTab = ({ projectData }) => {
  const { project, comments } = projectData ?? {};
  const [bodyContent, setBodyContent] = useState("");
  const [searchParam] = useSearchParams();

  const handleChange = (value) => {
    setBodyContent(value); // Update the body content
  };

  const bodyRef = useRef(null);
  const [triggerGetProjectData] = useLazyGetProjectByIdQuery();
  const [addComment, { isLoading: addCommentLoading }] =
    useAddProjectCommentMutation();
  
  // Use simple login check - no SIWE signature needed for comments
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector(selectGeneral);
  
  const {
    isLoading: getMeLoading,
    data,
    refetch: refetchUserData,
  } = useGetUserQuery(undefined, { skip: !isLoggedIn });
  const { user: userData } = data ?? {};

  // Scroll to comment input on mount
  React.useEffect(() => {
    scroller.scrollTo("bodyContent", {
      duration: 1000,
      smooth: "easeInOutQuint",
      offset: -330,
    });
    bodyRef?.current?.focus?.();
  }, []);

  const handleSubmitComment = async () => {
    // Just need to be logged in - no SIWE signature required for comments
    if (!isLoggedIn) {
      dispatch(setShowAuthModal({ show: true }));
      return;
    }
    
    try {
      const res = await addComment({
        id: project?._id,
        body: {
          body: bodyContent,
        },
      }).unwrap();
      setBodyContent("");
      await triggerGetProjectData({ id: project?._id }).unwrap();
      notify(res?.message, "success");
    } catch (error) {
      handleError(error, null, true);
    }
  };
  return (
    <div className="w-full flex flex-col pt-6">
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h5 className="text-xl font-bold text-secondary">Discussion</h5>
        <span className="ml-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
          {comments?.length || 0} comments
        </span>
      </div>
      
      <p className="text-gray-600 leading-relaxed mb-8 max-w-3xl">
        We invite you to engage in our vibrant discussion panel, where your
        valuable reviews, insights, and comments can make a real difference.
      </p>
      
      <div className="w-full max-w-4xl flex flex-col gap-y-6">
        {/* Comment Input Section */}
        <div className="p-5 rounded-2xl bg-gray-50/80 shadow-sm">
          <div className="flex items-start gap-4">
            <img
              src={userData?.images?.profile || UserPlaceHolderImg}
              alt={""}
              className="h-12 w-12 rounded-full shadow-md flex-shrink-0"
            />
            <div className="flex-1 flex flex-col gap-3">
              <ReactQuill
                value={bodyContent}
                onChange={handleChange}
                placeholder="Share your thoughts on this project..."
                className="w-full bg-white rounded-xl shadow-sm overflow-hidden [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:bg-gray-50 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[100px]"
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                  ],
                }}
                formats={[
                  "bold",
                  "italic",
                  "underline",
                  "strike",
                  "list",
                  "bullet",
                  "link",
                  "image",
                ]}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSubmitComment}
                  disabled={addCommentLoading || !bodyContent.trim()}
                  className="bg-gradient-to-r from-primary to-[#7e7cff] text-white flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 disabled:hover:scale-100"
                >
                  {addCommentLoading ? (
                    <Oval
                      height={18}
                      width={18}
                      color="#ffffff"
                      visible={true}
                      secondaryColor="#446CB3"
                      strokeWidth={3}
                    />
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Comments List */}
        {comments?.length ? (
          <div className="flex flex-col gap-4">
            {comments?.map((item, index) => (
              <div key={index} className="flex flex-col items-center w-full">
                <CommentItem
                  data={item}
                  key={"comment" + index}
                  useDeleteCommentMutation={useDeleteProjectCommentMutation}
                  useAddCommentMutation={useAddProjectCommentMutation}
                  useEditCommentMutation={useEditProjectCommentMutation}
                  refetch={triggerGetProjectData}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-lg font-medium">No comments yet</p>
            <p className="text-sm">Be the first to share your thoughts!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscussionTab;
